// @flow

import React from 'react';

import {
  getFormattedDateAndTime,
  getRetailTableCellObject,
  trackCN
} from '../../../../services/helpers';
import { RenderTableActionButtons } from '../../../commons/tableActionButton/tableActionButton';
import { zoomTableActionsConfig } from '../../../commons/zoomTable/zoomTableConfig';
import {
  BlockCNStatus,
  RtoUnblockDashboardStatuses
} from '../../../helper/Constants';

const rtoCnIcn = require('../../../../assets/images/rto_cn_icn.svg');
const unblockCnIcon = require('../../../../assets/images/unblock_cn_icn.svg');

export const headers = [
  { label: 'CN Number', key: 'cnote' },
  { label: 'From', key: 'fromLocation' },
  { label: 'To', key: 'toLocation' },
  { label: 'Created Date', key: 'createdAt' },
  { label: 'Updated Date', key: 'lastUpdatedAt' },
  { label: 'Movement Status', key: 'status' },
  { label: 'Reason', key: 'reason' }
];

export const unblockRtoCNButtonConfig = {
  buttons: [
    {
      // unblock button
      iconRef: unblockCnIcon,
      onClick: rowData => {
        if (!rowData.isActive) {
          return; // Do nothing if isActive is false
        }
        zoomTableActionsConfig.DAMAGE_CN_UNBLOCK_RTO_CONFIG.actions.unblockDamagePilferageCns(
          rowData
        );
      },
      style: {
        width: '40%', // Increased width
        height: '50%', // Increased height
        margin: '5%',
        opacity: rowData => (rowData.isActive ? 1 : 0.25),
        pointerEvents: rowData => (rowData.isActive ? 'auto' : 'none'),
        border: 'none', // Optional: remove border
        borderRadius: '5px' // Optional: rounded corners
      },
      hide: rowData => !rowData.isActive
    },
    {
      // RTO button
      iconRef: rtoCnIcn,
      onClick: rowData => {
        if (!rowData.isActive) {
          return; // Do nothing if isActive is false
        }
        zoomTableActionsConfig.DAMAGE_CN_UNBLOCK_RTO_CONFIG.actions.rtoDamagePilferageCns(
          rowData
        );
      },
      style: {
        width: '40%', // Increased width
        height: '50%', // Increased height
        margin: '5%',
        opacity: rowData => (rowData.isActive ? 1 : 0.25),
        pointerEvents: rowData => (rowData.isActive ? 'auto' : 'none'),
        border: 'none', // Optional: remove border
        borderRadius: '5px' // Optional: rounded corners
      },
      hide: rowData => !rowData.isActive
    }
  ]
};

export const damageCnUnblockAndRtoDashboardConfig = {
  // config for internal users
  columns: [
    {
      headerClassName: 'flex-11 zt-custom-th',
      className: 'zt-custom-td flex-11',
      Header: headers[0].label,
      sortable: false,
      key: headers[0].key,
      Cell: (row: Object) => (
        <div className="l-row a-center">
          {getRetailTableCellObject(
            row.original && row.original.cnote,
            '',
            row.original,
            trackCN,
            'cn-highlight'
          )}
        </div>
      )
    },
    {
      Header: headers[1].label,
      headerClassName: 'flex-11 zt-custom-th',
      className: 'zt-custom-td flex-11',
      sortable: true,
      key: headers[1].key,
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original && row.original.fromLocation,
          '',
          row.original
        )
    },
    {
      Header: headers[2].label,
      headerClassName: 'flex-11 zt-custom-th',
      className: 'zt-custom-td flex-11',
      sortable: true,
      key: headers[2].key,
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original && row.original && row.original.toLocation,
          '',
          row.original
        )
    },
    {
      Header: headers[3].label,
      headerClassName: 'flex-11 zt-custom-th',
      className: 'zt-custom-td flex-11',
      sortable: true,
      key: headers[4].key,
      formatter: (row: Object) =>
        row && row.createdAt && getFormattedDateAndTime(row.createdAt),
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original &&
            row.original.createdAt &&
            getFormattedDateAndTime(row.original.createdAt),
          '',
          row.original
        )
    },
    {
      Header: headers[4].label,
      headerClassName: 'flex-11 zt-custom-th',
      className: 'zt-custom-td flex-11',
      sortable: true,
      key: headers[4].key,
      formatter: (row: Object) =>
        row && row.lastUpdatedAt && getFormattedDateAndTime(row.lastUpdatedAt),
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original &&
            row.original.lastUpdatedAt &&
            getFormattedDateAndTime(row.original.lastUpdatedAt),
          '',
          row.original
        )
    },
    {
      Header: headers[5].label,
      headerClassName: 'flex-11 zt-custom-th',
      className: 'zt-custom-td flex-11',
      sortable: false,
      key: headers[5].key,
      formatter: (row: Object) => {
        if (row && row.isActive) {
          return BlockCNStatus.BLOCKED;
        }
        if (row && row.isRTO) {
          return BlockCNStatus.RTO; // Check for RTO status
        }
        return BlockCNStatus.UNBLOCKED;
      },
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original && row.original.isActive
            ? BlockCNStatus.BLOCKED
            : row.original && row.original.isRTO // Check for RTO status
            ? BlockCNStatus.RTO
            : BlockCNStatus.UNBLOCKED,
          '',
          row.original
        )
    },
    {
      Header: headers[6].label,
      headerClassName: 'flex-14 zt-custom-th',
      className: 'zt-custom-td flex-14',
      sortable: false,
      key: headers[6].key,
      formatter: (row: Object) => {
        if (row && row.isActive) {
          return RtoUnblockDashboardStatuses.BLOCKED_CN;
        }
        if (row && row.isRTO) {
          return RtoUnblockDashboardStatuses.RTO_CN; // Check for RTO status
        }
        return RtoUnblockDashboardStatuses.UNBLOCKED_CN;
      },
      Cell: (row: Object) => {
        return getRetailTableCellObject(
          row.original && row.original.isActive
            ? RtoUnblockDashboardStatuses.BLOCKED_CN
            : row.original && row.original.isRTO // Check for RTO status
            ? RtoUnblockDashboardStatuses.RTO_CN
            : RtoUnblockDashboardStatuses.UNBLOCKED_CN,
          '',
          row.original
        );
      }
    },
    {
      Header: 'Action',
      headerClassName: 'flex-11 zt-custom-th zt-th-action',
      className: 'zt-custom-td flex-11 zt-th-action',
      Cell: (row: Object) => (
        <RenderTableActionButtons
          buttonConfig={unblockRtoCNButtonConfig.buttons}
          rowData={row.original || {}}
        />
      )
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: "No CN's to Track",
  minRows: 0,
  pageSizeOptions: [7, 10, 15, 25, 50, 100],
  style: { maxHeight: 'calc(100vh - 18rem)' }
};

export const damageCnUnblockAndRtoDashboardTableActionConfig = {};
